import { defineStore } from 'pinia'
import { useAuthStore } from './useAuthStore'
import { ref, watch, computed } from 'vue'
import {
  fetchUserAllocationAlerts,
  removeAllocationAlert,
} from '~/apps/allocation-alert/service'
import { UserAllocationAlert } from '~/apps/allocation-alert/types'

export const useUserAllocationAlertsStore = defineStore(
  'userAllocationAlertsStore',
  () => {
    const authStore = useAuthStore(usePinia())
    const allocationAlerts = ref<UserAllocationAlert[]>([])
    const isLoading = ref(true)

    if (process.client) {
      watch(
        () => authStore.isLoggedIn,
        async (value, oldValue) => {
          if (value && value !== oldValue) {
            await fetchAlerts()
          }
          if (!value && value !== oldValue) {
            allocationAlerts.value = []
          }
        },
      )
    }

    async function fetchAlerts() {
      if (!authStore.isLoggedIn) return
      isLoading.value = true
      const { locale } = useI18n().i18n
      allocationAlerts.value = await fetchUserAllocationAlerts(locale)
      isLoading.value = false
    }

    async function removeAlert(alertId: string, alertType: string) {
      await removeAllocationAlert(alertId, alertType)
      allocationAlerts.value = allocationAlerts.value.filter((alert) => {
        const hasMatchingId = alert.data.id === alertId
        const hasMatchingType = alert.type === alertType
        return !(hasMatchingId && hasMatchingType)
      })
    }

    const pastAlerts = computed(() => {
      return allocationAlerts.value
        .filter(
          (alert) =>
            (alert.type === 'allocation_alert' ||
              alert.type === 'location_allocation_alert') &&
            !alert.data.isFuture,
        )
        .sort(
          (a, b) =>
            new Date(a.data.arrive).getTime() -
            new Date(b.data.arrive).getTime(),
        )
    })

    const futureAlerts = computed(() => {
      return allocationAlerts.value
        .filter(
          (alert) =>
            (alert.type === 'allocation_alert' ||
              alert.type === 'location_allocation_alert') &&
            alert.data.isFuture,
        )
        .sort((a, b) => {
          // First sort by availability (true values first)
          if (a.data.hasAvailability && !b.data.hasAvailability) return -1
          if (!a.data.hasAvailability && b.data.hasAvailability) return 1
          // Then sort by arrival date
          return (
            new Date(a.data.arrive).getTime() -
            new Date(b.data.arrive).getTime()
          )
        })
    })

    return {
      allocationAlerts,
      fetchAlerts,
      futureAlerts,
      isLoading,
      pastAlerts,
      removeAlert,
    }
  },
)
